import type {
  CampActionResult,
  CampImportCrosswalkDetail,
  CampImportCrosswalkQueueResponse,
  CampProgramEditorResponse,
  CampRegistration,
  CampRegistrationDraft,
  CampRegistrationListResponse,
  CampRosterExportRecord,
  CampWaitlistEntry,
  CampWaitlistListResponse,
  ClearCampCrosswalkLinkInput,
  ConfirmCampShadowFamilyLinkInput,
  ConfirmCampShadowStudentLinkInput,
  GetParentCatalogInput,
  GetParentRegistrationInput,
  GetCampImportCrosswalkDetailInput,
  GetProgramEditorInput,
  GetProgramInput,
  GetSchoolDashboardInput,
  JoinWaitlistInput,
  ListCampImportCrosswalkQueueInput,
  ListParentRegistrationsInput,
  ListParentWaitlistInput,
  ParentCampCatalogResponse,
  ParentCampRegistrationResponse,
  RecomputeCampCrosswalkCandidatesInput,
  RejectCampShadowFamilyCandidatesInput,
  RejectCampShadowStudentCandidatesInput,
  SaveRegistrationDraftInput,
  SchoolCampDashboardResponse,
  SchoolCampProgramDetailResponse,
  SubmitRegistrationInput,
  CancelRegistrationInput,
  LeaveWaitlistInput,
  CreateProgramInput,
  UpdateProgramInput,
  PublishProgramInput,
  ArchiveProgramInput,
  UpdateSessionCapacityInput,
  PromoteWaitlistEntryInput,
  ManualEnrollCamperInput,
  RequestRosterExportInput,
  IssueRefundInput,
  TriggerCampCommunicationInput,
  CampProgram,
  CampSession,
} from '../contracts/index';
import type { DomainRouterJsonInput } from './domain-router-transport';

export const CAMPS_QUERY_OPERATION_IDS = [
  'getParentCatalog',
  'getParentRegistration',
  'listParentWaitlist',
  'listParentRegistrations',
  'getSchoolDashboard',
  'getProgram',
  'getProgramEditor',
  'listCampImportCrosswalkQueue',
  'getCampImportCrosswalkDetail',
] as const;

export const CAMPS_MUTATION_OPERATION_IDS = [
  'saveRegistrationDraft',
  'submitRegistration',
  'cancelRegistration',
  'joinWaitlist',
  'leaveWaitlist',
  'createProgram',
  'updateProgram',
  'publishProgram',
  'archiveProgram',
  'updateSessionCapacity',
  'promoteWaitlistEntry',
  'manualEnrollCamper',
  'requestRosterExport',
  'issueRefund',
  'triggerCampCommunication',
  'confirmCampShadowFamilyLink',
  'confirmCampShadowStudentLink',
  'rejectCampShadowFamilyCandidates',
  'rejectCampShadowStudentCandidates',
  'clearCampCrosswalkLink',
  'recomputeCampCrosswalkCandidates',
] as const;

export type CampsQueryOperationId = (typeof CAMPS_QUERY_OPERATION_IDS)[number];
export type CampsMutationOperationId = (typeof CAMPS_MUTATION_OPERATION_IDS)[number];

export type CampsQueryArgsMap = {
  getParentCatalog: { input?: GetParentCatalogInput | DomainRouterJsonInput };
  getParentRegistration: { input: GetParentRegistrationInput | DomainRouterJsonInput };
  listParentWaitlist: { input?: ListParentWaitlistInput | DomainRouterJsonInput };
  listParentRegistrations: { input?: ListParentRegistrationsInput | DomainRouterJsonInput };
  getSchoolDashboard: { input?: GetSchoolDashboardInput | DomainRouterJsonInput };
  getProgram: { input: GetProgramInput | DomainRouterJsonInput };
  getProgramEditor: { input?: GetProgramEditorInput | DomainRouterJsonInput };
  listCampImportCrosswalkQueue: { input?: ListCampImportCrosswalkQueueInput | DomainRouterJsonInput };
  getCampImportCrosswalkDetail: { input: GetCampImportCrosswalkDetailInput | DomainRouterJsonInput };
};

export type CampsQueryResultMap = {
  getParentCatalog: ParentCampCatalogResponse;
  getParentRegistration: ParentCampRegistrationResponse;
  listParentWaitlist: CampWaitlistListResponse;
  listParentRegistrations: CampRegistrationListResponse;
  getSchoolDashboard: SchoolCampDashboardResponse;
  getProgram: SchoolCampProgramDetailResponse;
  getProgramEditor: CampProgramEditorResponse;
  listCampImportCrosswalkQueue: CampImportCrosswalkQueueResponse;
  getCampImportCrosswalkDetail: CampImportCrosswalkDetail;
};

export type CampsMutationArgsMap = {
  saveRegistrationDraft: { input: SaveRegistrationDraftInput | DomainRouterJsonInput };
  submitRegistration: { input: SubmitRegistrationInput | DomainRouterJsonInput };
  cancelRegistration: { input: CancelRegistrationInput | DomainRouterJsonInput };
  joinWaitlist: { input: JoinWaitlistInput | DomainRouterJsonInput };
  leaveWaitlist: { input: LeaveWaitlistInput | DomainRouterJsonInput };
  createProgram: { input: CreateProgramInput | DomainRouterJsonInput };
  updateProgram: { input: UpdateProgramInput | DomainRouterJsonInput };
  publishProgram: { input: PublishProgramInput | DomainRouterJsonInput };
  archiveProgram: { input: ArchiveProgramInput | DomainRouterJsonInput };
  updateSessionCapacity: { input: UpdateSessionCapacityInput | DomainRouterJsonInput };
  promoteWaitlistEntry: { input: PromoteWaitlistEntryInput | DomainRouterJsonInput };
  manualEnrollCamper: { input: ManualEnrollCamperInput | DomainRouterJsonInput };
  requestRosterExport: { input: RequestRosterExportInput | DomainRouterJsonInput };
  issueRefund: { input: IssueRefundInput | DomainRouterJsonInput };
  triggerCampCommunication: { input: TriggerCampCommunicationInput | DomainRouterJsonInput };
  confirmCampShadowFamilyLink: { input: ConfirmCampShadowFamilyLinkInput | DomainRouterJsonInput };
  confirmCampShadowStudentLink: { input: ConfirmCampShadowStudentLinkInput | DomainRouterJsonInput };
  rejectCampShadowFamilyCandidates: { input: RejectCampShadowFamilyCandidatesInput | DomainRouterJsonInput };
  rejectCampShadowStudentCandidates: { input: RejectCampShadowStudentCandidatesInput | DomainRouterJsonInput };
  clearCampCrosswalkLink: { input: ClearCampCrosswalkLinkInput | DomainRouterJsonInput };
  recomputeCampCrosswalkCandidates: { input: RecomputeCampCrosswalkCandidatesInput | DomainRouterJsonInput };
};

export type CampsMutationResultMap = {
  saveRegistrationDraft: CampRegistrationDraft;
  submitRegistration: CampRegistrationListResponse;
  cancelRegistration: CampRegistration;
  joinWaitlist: CampWaitlistEntry;
  leaveWaitlist: CampWaitlistEntry;
  createProgram: CampProgramEditorResponse;
  updateProgram: CampProgramEditorResponse;
  publishProgram: CampProgram;
  archiveProgram: CampProgram;
  updateSessionCapacity: CampSession;
  promoteWaitlistEntry: CampRegistration;
  manualEnrollCamper: CampRegistration;
  requestRosterExport: CampRosterExportRecord;
  issueRefund: CampRegistration;
  triggerCampCommunication: CampActionResult;
  confirmCampShadowFamilyLink: CampImportCrosswalkDetail;
  confirmCampShadowStudentLink: CampImportCrosswalkDetail;
  rejectCampShadowFamilyCandidates: CampImportCrosswalkDetail;
  rejectCampShadowStudentCandidates: CampImportCrosswalkDetail;
  clearCampCrosswalkLink: CampImportCrosswalkDetail;
  recomputeCampCrosswalkCandidates: CampImportCrosswalkDetail;
};
